// @flow weak
/*
 * Radial progress bar. Takes a pixel diameter and a progress between 0 and 1
 * and fills in a circle with the proper amount of progress.
 *
 * Depends on dynamically setting stroke-dashoffset to fill in an svg circle.
 */

import PropTypes from 'prop-types';
import React from 'react';
import bemify from 'bemify-js';

import Progress from 'app/common/components/Progress';
import partiallyApplyComponent from 'app/common/components/partiallyApplyComponent';
import 'app/common/components/ProgressCircle.scss';

const ProgressCircle = props => {
  const bem = bemify('progress-circle');

  const { barWidth, decimal, diameter } = props;
  const center = diameter / 2;
  const radius = diameter / 2 - barWidth / 2;
  const circumference = Math.PI * (radius * 2);

  // How much to offset, which is how much of the progress bar NOT to show.
  const offset = (1 - decimal) * circumference;

  const circleProps = {
    cx: center,
    cy: center,
    r: radius,
  };

  const barStyle = {
    strokeWidth: barWidth,
    strokeDashoffset: offset,
    strokeDasharray: circumference,
    stroke: props.barColor,
  };

  const circleStyle = {
    fill: props.fill,
    strokeWidth: barWidth,
    stroke: props.strokeColor,
  };

  const className = bem({
    '--can-click': props.canClick,
    '--rounded': props.isRounded,
    [`--${props.type}`]: !!props.type,
    [`--${props.orientation}`]: props.orientation,
  });

  return (
    <div className={className} title={props.title || props.formattedPercent}>
      <svg
        className={bem('__svg')}
        height={diameter}
        viewBox={`0 0 ${diameter} ${diameter}`}
        width={diameter}
      >
        {/* Full circle that always shows. */}
        <circle
          {...circleProps}
          className={bem('__circle')}
          style={circleStyle}
        />
        {/* Circle used for progress. */}
        <circle {...circleProps} className={bem('__bar')} style={barStyle} />
      </svg>
    </div>
  );
};

ProgressCircle.propTypes = {
  barWidth: PropTypes.number.isRequired,
  barColor: PropTypes.string,
  canClick: PropTypes.bool.isRequired,
  isRounded: PropTypes.bool.isRequired,
  decimal: PropTypes.number.isRequired,
  diameter: PropTypes.number.isRequired,
  fill: PropTypes.string.isRequired,
  strokeColor: PropTypes.string,
  formattedPercent: PropTypes.string.isRequired,
  max: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  title: PropTypes.string,
  orientation: PropTypes.oneOf(['top', 'bottom']).isRequired,
};

ProgressCircle.defaultProps = {
  barWidth: 2,
  canClick: false,
  isRounded: false,
  diameter: 70,
  fill: 'transparent',
  type: 'primary',
  orientation: 'top',
};

export default partiallyApplyComponent(Progress, { Renderer: ProgressCircle });
